import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Input, InputProps } from "reactstrap";
import usePostalCode, { getPostalCode } from "../../../hook/usePostalCode";
import { IOption } from "../../../types/common";
import SearchInput from "./search-input";

interface PostalCodeInputProps extends InputProps {
  innerName: string;
  onConfirm?: (id?: number, postalCode?: string) => void;
}

const PostalCodeInput = (props: PostalCodeInputProps) => {
  const { innerName, onConfirm, value, onChange, ...inputProps } = props;

  const isControlled = 'value' in props;

  const [postalCodes] = usePostalCode();

  const [options, setOptions] = useState<IOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<IOption[]>([]);

  const [idValue, setIdValue] = useState('');
  const [textValue, setTextValue] = useState('');

  const idInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setOptions(postalCodes.map(item => (
      {
        id: item.id,
        label: item.name
      }
    )));

    if (value) {
      setIdValue(value ? value.toString() : '');
    }
  }, [postalCodes, value]);

  useEffect(() => {
    let postalCode: string | undefined;
    if (idValue && +idValue > 0) {
      postalCode = getPostalCode(postalCodes, +idValue) ?? '';
      setTextValue(postalCode);
    }

    if (!isControlled && idInputRef.current) {
      idInputRef.current.value = idValue ?? '';
    }

    onConfirm?.(+idValue, postalCode);
  }, [postalCodes, idValue]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTextValue(value.toUpperCase());
    if (!isControlled) {
      event.target.value = value.toUpperCase();
    }
    setIdValue('0');

    const rawValue = value.replaceAll(' ', '');
    if (rawValue.length === 0) {
      setIdValue('');
    } else if (rawValue.length === 6) {
      const postalCode = postalCodes.find(item => item.name.replaceAll(' ', '') === rawValue);
      if (postalCode) {
        setIdValue(postalCode.id.toString());
      }
    }

    onChange?.(event);
  };

  const handleSearch = (str: string) =>
    setFilteredOptions(
      options?.filter(c => c.label.replace(/\s/g, '').toLowerCase()
        .startsWith(str.replace(/\s/g, '').toLowerCase()))
    );

  const handleConfirm = (option: IOption) => setIdValue(option.id.toString());

  return (isControlled
    ? <>
      <SearchInput auto {...inputProps}
        maxLength={7}
        value={textValue}
        onChange={handleChange}
        options={filteredOptions}
        onSearch={handleSearch}
        onConfirm={handleConfirm} />
      <Input className="d-none"
        type="number"
        name={innerName}
        value={idValue}
        onChange={() => { }} />
    </>
    : <>
      <SearchInput auto {...inputProps}
        maxLength={7}
        onChange={handleChange}
        options={filteredOptions}
        onSearch={handleSearch}
        onConfirm={handleConfirm} />
      <Input className="d-none"
        type="number"
        name={innerName}
        innerRef={idInputRef} />
    </>
  );
};

export default PostalCodeInput;
